import { throttle } from 'lodash-es';
import {
  type IGlobalTab,
  TabType,
  TabTypeRumViewMap,
} from '@carboncopyco/types/desktop';
import {
  removeRumGlobalContextProperty,
  setRumGlobalContextProperty,
  startRumView,
} from '@carboncopyco/logger';
import {
  isHomeTabType,
  isPresentDeeplinkTab,
} from '../../shared/globalTabUtil';
import { useTabs } from '../store/context';

const getTabGroupStates = ({
  tab,
  globalTabs,
}: {
  tab: IGlobalTab;
  globalTabs: IGlobalTab[];
}) => {
  const groupRelatedTabs = globalTabs?.filter(
    ({ group }) => group && group.id === tab.group?.id
  );

  return (
    tab.group && {
      groupRelatedTabs,
      isGroupingTab: tab.group,
      isVisibleIfGroupIsActive: tab.group.isVisibleIfGroupIsActive,
      isVisibleIfGroupIsInactive: tab.group.isVisibleIfGroupIsInactive,
      showNonRelatedGroupTabsIfActive:
        tab.group.showNonRelatedGroupTabsIfActive,
    }
  );
};

const getTabStates = (tab: IGlobalTab, globalTabs: IGlobalTab[]) => {
  const group = getTabGroupStates({ tab, globalTabs });

  return {
    group,
  };
};

const getHiddenState = (
  tab: IGlobalTab,
  tabToSwitchTo: IGlobalTab,
  globalTabs: IGlobalTab[]
) => {
  const { group } = getTabStates(tab, globalTabs);
  const tabToSwitchToIsGroupingTab = !!tabToSwitchTo.group;

  const isActiveTab = tabToSwitchTo.id === tab.id;

  if (
    !isHomeTabType(tab) &&
    !isActiveTab &&
    isPresentDeeplinkTab(tabToSwitchTo)
  ) {
    return true;
  }

  if (group) {
    const isActiveGroupingTab = group.groupRelatedTabs?.some(
      ({ id }) => id === tabToSwitchTo.id
    );

    if (isActiveGroupingTab) {
      return !group.isVisibleIfGroupIsActive;
    }
    if (!isActiveGroupingTab) {
      if (tabToSwitchToIsGroupingTab && group.isVisibleIfGroupIsInactive) {
        return (
          tabToSwitchTo.group &&
          !tabToSwitchTo.group?.showNonRelatedGroupTabsIfActive
        );
      }
      return !group.isVisibleIfGroupIsInactive;
    }
  }

  if (tabToSwitchTo.group) {
    return !tabToSwitchTo.group?.showNonRelatedGroupTabsIfActive;
  }

  return false;
};

const throttledStartRumView = throttle((tabToSwitchTo: IGlobalTab) => {
  if (tabToSwitchTo.type === TabType.BASIC) {
    startRumView('home');
    removeRumGlobalContextProperty('tab');
  } else {
    startRumView(TabTypeRumViewMap[tabToSwitchTo.type]);
    setRumGlobalContextProperty('tab', tabToSwitchTo);
  }
}, 500);

const useSwitchTab = () => {
  const { setGlobalTabs, setActiveGlobalTabId, globalTabsRef } = useTabs();
  return (
    tabToSwitchTo: IGlobalTab,
    options?: {
      globalTabs?: IGlobalTab[];
    }
  ) => {
    const updatedGlobalTabs = (
      options?.globalTabs || globalTabsRef.current
    ).map((tab) => ({
      ...tab,
      hidden: getHiddenState(
        tab,
        tabToSwitchTo,
        options?.globalTabs || globalTabsRef.current
      ),
    }));

    setGlobalTabs(updatedGlobalTabs);
    setActiveGlobalTabId(tabToSwitchTo.id);

    throttledStartRumView(tabToSwitchTo);
    return updatedGlobalTabs;
  };
};

export default useSwitchTab;
